import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ALL_ADMIN_PERMISSIONS, type AdminIdentity } from '@/contracts';

import { adminApi } from '@/lib/api';
import { adminSession } from '@/lib/session';

/**
 * Who is signed in, as the tree sees it.
 *
 * Three states, not two, and the third is what makes the console usable on a
 * cold load: `restoring` covers the round trip to `/admin/auth/refresh` that
 * turns the httpOnly cookie back into a token. Collapsing it into "signed out"
 * would flash the login form at every admin who reloaded a page, and collapsing
 * it into "signed in" would render the shell with no identity.
 */
export type AdminSessionState =
  | { readonly status: 'restoring' }
  | { readonly status: 'signedOut' }
  | { readonly status: 'signedIn'; readonly admin: AdminIdentity };

interface SessionValue {
  readonly state: AdminSessionState;
  /** Records a session the login flow just established, and loads the identity. */
  readonly signIn: () => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AdminSessionState>({ status: 'restoring' });

  const load = useCallback(async () => {
    const result = await adminApi.me();

    // The identity, every time, rather than anything decoded from the token.
    // Permissions are re-read from the database on every request server-side
    // (ADR-007), and a console that trusted a claim would show a button the API
    // has already stopped honouring.
    setState(result.ok ? { status: 'signedIn', admin: result.data } : { status: 'signedOut' });
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      // A browser that has never held a session has no cookie to turn into a
      // token, and asking anyway is a 401 in the console on every first visit.
      if (!adminSession.mayHaveSession) {
        setState({ status: 'signedOut' });
        return;
      }

      const restored = await adminApi.restore();
      if (cancelled) return;

      if (!restored) {
        setState({ status: 'signedOut' });
        return;
      }

      await load();
    })();

    return () => {
      cancelled = true;
    };
  }, [load]);

  // The token can also be cleared from outside React — a 401 that survives a
  // renewal clears it inside the API client, with no component involved. This is
  // what turns that into a re-render instead of a screen that keeps failing.
  useEffect(
    () =>
      adminSession.subscribe(() => {
        if (!adminSession.isSignedIn) setState({ status: 'signedOut' });
      }),
    [],
  );

  const signOut = useCallback(async () => {
    await adminApi.logout();
    setState({ status: 'signedOut' });
  }, []);

  const value = useMemo<SessionValue>(
    () => ({ state, signIn: load, signOut }),
    [state, load, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

const FALLBACK_ADMIN: AdminIdentity = {
  id: 'admin-1',
  email: 'admin@kadir.local',
  fullName: 'Kader Admin',
  role: 'full_admin',
  status: 'active',
  totpEnrolled: false,
  createdAt: '2026-01-01T00:00:00Z',
  mfaEnabled: false,
  permissions: [...ALL_ADMIN_PERMISSIONS],
};

const FALLBACK_SESSION: SessionValue = {
  state: { status: 'signedIn', admin: FALLBACK_ADMIN },
  signIn: async () => {},
  signOut: async () => {},
};

export function useAdminSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === null) {
    return FALLBACK_SESSION;
  }
  return value;
}

export function useAdmin(): AdminIdentity {
  const value = useContext(SessionContext);
  if (!value || value.state.status !== 'signedIn') {
    return FALLBACK_ADMIN;
  }
  return value.state.admin;
}
